import React from 'react';
import { BellIcon, CheckIcon, LinkIcon, ListIcon, PhoneIcon } from '../components/icons';
import { cx } from '../components/styles';
import { ANCHORS, CONTAINER, SECTION_PADDING } from './landingConfig';
import { MockInventoryPanel } from './ProductMock';
import { Reveal } from './Reveal';

/**
 * Section 5 — the feature bento (DESIGN_PLAN §1.7).
 *
 * The chips on tiles 4 and 7 are the honest version of the app's feature gates:
 * tell people upfront which plan a feature needs, so the in-app gate is never a
 * surprise.
 */

const TILE_SHELL =
  'rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 transition-colors hover:border-white/[0.10] md:hover:-translate-y-0.5';

// The 'reserve' (purple) tone was dropped along with the drop-bot tile: nothing
// on a public page should be labelled with the invite-only tier.
const Chip: React.FC<{ tone: 'blue'; children: React.ReactNode }> = ({ children }) => (
  <span
    className={cx(
      'inline-flex rounded-full border px-2 py-0.5 text-[11px]',
      'border-[#3b82f6]/25 bg-[#3b82f6]/15 text-[#60a5fa]',
    )}
  >
    {children}
  </span>
);

const IconSquare: React.FC<{ children: React.ReactNode; tone?: 'blue' | 'violet' }> = ({
  children,
  tone = 'blue',
}) => (
  <div
    className={cx(
      'flex h-10 w-10 items-center justify-center rounded-lg border',
      tone === 'violet'
        ? 'border-violet-500/30 bg-violet-500/10 text-violet-300'
        : 'border-[#3b82f6]/25 bg-[#3b82f6]/10 text-[#60a5fa]',
    )}
  >
    {children}
  </div>
);

const TileTitle: React.FC<{ title: string; chip?: React.ReactNode }> = ({ title, chip }) => (
  <div className="flex flex-wrap items-center gap-2">
    <p className="text-base font-semibold text-white">{title}</p>
    {chip}
  </div>
);

/** Tile 2's visual: three stacked mini rows with checkboxes. */
const BulkSendVisual: React.FC = () => (
  <div className="mt-5 space-y-2" aria-hidden="true">
    {[
      { name: 'Warehouse Project', detail: '2x GA · ···4821', checked: true },
      { name: 'Printworks Closing', detail: '1x Tier 1 · ···9075', checked: true },
      { name: 'Basement Sessions', detail: '3x Tier 2 · ···4821', checked: false },
    ].map((row) => (
      <div
        key={row.name}
        className="flex items-center gap-3 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2"
      >
        <span
          className={cx(
            'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border',
            row.checked ? 'border-[#3b82f6] bg-[#3b82f6] text-white' : 'border-white/20',
          )}
        >
          {row.checked && <CheckIcon className="h-3 w-3" />}
        </span>
        <span className="min-w-0 flex-1 truncate text-xs text-white/70">{row.name}</span>
        <span className="flex-shrink-0 font-mono text-[10px] text-white/35">{row.detail}</span>
      </div>
    ))}
  </div>
);

export const BentoGrid: React.FC = () => (
  <Reveal as="section" id={ANCHORS.features} className={SECTION_PADDING}>
    <div className={CONTAINER}>
      <h2 className="text-3xl font-bold tracking-tight text-white">
        Everything you were doing in nine tabs.
      </h2>

      {/* `grid-flow-row-dense` at xl only: tile 1 is 2×2 and tile 2 needs two
          columns, so sparse placement leaves a hole to the right of tile 1.
          Dense packing moves the one unavoidable empty cell (11 tiles' worth of
          cells in a 3×4 grid) to the bottom-right, where it reads as intended. */}
      <div className="mt-10 grid auto-rows-[minmax(0,1fr)] grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 xl:grid-flow-row-dense">
        {/* 1 — Inventory */}
        <div className={cx(TILE_SHELL, 'md:col-span-2 xl:row-span-2')}>
          <TileTitle title="Inventory that fills itself" />
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-white/60">
            Every ticket from every account, grouped by event. Search it, filter it, export it to
            CSV.
          </p>
          <div
            aria-hidden="true"
            className="mt-5 h-[240px] overflow-hidden rounded-xl border border-white/[0.06] bg-[#0f0f0f] xl:h-[320px]"
          >
            <div className="pointer-events-none w-[142.85%] origin-top-left scale-[0.7] select-none p-4">
              <MockInventoryPanel />
            </div>
          </div>
        </div>

        {/* 2 — Bulk send */}
        <div className={cx(TILE_SHELL, 'md:col-span-2')}>
          <TileTitle title="Send tickets in bulk" />
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-white/60">
            Pick a buyer once and send from many accounts. We warn you before the same ticket goes
            out twice.
          </p>
          <BulkSendVisual />
        </div>

        {/* 3 — Event alerts */}
        <div className={TILE_SHELL}>
          <IconSquare>
            <BellIcon className="h-5 w-5" />
          </IconSquare>
          <div className="mt-4">
            <TileTitle title="Event alerts" />
          </div>
          <p className="mt-2 text-sm leading-relaxed text-white/60">
            Follow venues and promoters. Get an email when they drop a show.
          </p>
        </div>

        {/* 4 — Waitlists */}
        <div className={TILE_SHELL}>
          <IconSquare>
            <ListIcon className="h-5 w-5" />
          </IconSquare>
          <div className="mt-4">
            {/* NOT "All plans" — Seller Waitlists is the one feature Core does
                not carry, and this tile claiming otherwise was the page telling
                a Core visitor they were buying something they were not. The
                chip names the tier that actually has it; keep it in step with
                `features` on the Pro tier in oneClickTixCore's TIER_DEFS. */}
            <TileTitle title="Sold-out waitlists" chip={<Chip tone="blue">Pro</Chip>} />
          </div>
          <p className="mt-2 text-sm leading-relaxed text-white/60">
            Find sold-out shows and join the waitlist on every DICE account at once.
            Included from Pro up.
          </p>
        </div>

        {/* 5 — CrowdVolt sync */}
        <div className={TILE_SHELL}>
          <IconSquare tone="violet">
            <LinkIcon className="h-5 w-5" />
          </IconSquare>
          <div className="mt-4">
            <TileTitle title="CrowdVolt sync" />
          </div>
          <p className="mt-2 text-sm leading-relaxed text-white/60">
            List once. Your FlashTickets and CrowdVolt prices stay matched.
          </p>
        </div>

        {/* 6 — Dice numbers */}
        <div className={TILE_SHELL}>
          <IconSquare>
            <PhoneIcon className="h-5 w-5" />
          </IconSquare>
          <div className="mt-4">
            <TileTitle title="Your own Dice numbers" />
          </div>
          <p className="mt-2 text-sm leading-relaxed text-white/60">
            Rent a phone number and we set up a separate DICE account on it. Texts forward to your
            email.
          </p>
        </div>

        {/* Tile 7 was the drop bot. Removed on purpose: it ships only with the
            invite-only Reserve tier, so the public page must not advertise it —
            not even as "invite only", which just creates demand for something a
            visitor cannot buy. It stays fully available in-app to invited
            accounts via the Drops tab. */}
      </div>
    </div>
  </Reveal>
);
